import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FleetViewDevice,
  FleetViewFilterKeys,
  FleetViewSort,
  LiveRiskDecision,
} from './types';
import { DeviceDeploymentStatus } from '../devices/types';
import { AddressEntity } from '../addresses/types';
import { RootState } from '../store';
import { effectiveSort, hasLiveRisk } from '../../utils/fleetLiveRisk';

interface FleetViewState {
  selectedSerialNumber: string | null;
  selectedCompany: string | null;
  devices: FleetViewDevice[];
  searchQuery?: string;
  filter: { [K in keyof typeof FleetViewFilterKeys]: boolean };
  sort: FleetViewSort | null;
  selectedDeploymentStatus: DeviceDeploymentStatus | null;
  selectedDecision: LiveRiskDecision | null;
  selectedAddress: AddressEntity | null;
}

const initialState: FleetViewState = {
  devices: [],
  selectedSerialNumber: null,
  filter: Object.fromEntries(
    Object.keys(FleetViewFilterKeys).map((key) => [key, true])
  ) as { [K in keyof typeof FleetViewFilterKeys]: boolean },
  sort: null,
  selectedCompany: null,
  selectedDeploymentStatus: null,
  selectedDecision: null,
  selectedAddress: null,
};

const fleetViewSlice = createSlice({
  name: 'fleetView',
  initialState,
  reducers: {
    setSelectedSerialNumber(state, action: PayloadAction<string | null>) {
      state.selectedSerialNumber = action.payload;
    },
    setSelectedCompany(state, action: PayloadAction<string | null>) {
      state.selectedCompany = action.payload;
    },

    setDevices(state, action: PayloadAction<FleetViewDevice[]>) {
      state.devices = action.payload;
      if (!hasLiveRisk(action.payload)) {
        state.selectedDecision = null;
      }
    },
    setSearchQuery(state, action: PayloadAction<string | undefined>) {
      state.searchQuery = action.payload;
    },
    toggleFilter(
      state,
      action: PayloadAction<{ filter: keyof typeof FleetViewFilterKeys }>
    ) {
      const newFilter = {
        ...state.filter,
        [action.payload.filter]: !state.filter[action.payload.filter],
      };
      state.filter = newFilter;
    },
    setSort(state, action: PayloadAction<FleetViewSort>) {
      state.sort = action.payload;
    },
    setSelectedDeploymentStatus(
      state,
      action: PayloadAction<DeviceDeploymentStatus | null>
    ) {
      state.selectedDeploymentStatus = action.payload;
    },
    setSelectedDecision(state, action: PayloadAction<LiveRiskDecision | null>) {
      state.selectedDecision = action.payload;
    },
    setSelectedAddress: (state, { payload }: PayloadAction<AddressEntity>) => {
      state.selectedAddress = payload;
    },
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
    },
  },
});

export const {
  setSelectedSerialNumber,
  setSelectedCompany,
  setDevices,
  setSearchQuery,
  toggleFilter,
  setSort,
  setSelectedDeploymentStatus,
  setSelectedDecision,
  setSelectedAddress,
  clearSelectedAddress,
} = fleetViewSlice.actions;

export const selectEffectiveSort = (state: Pick<RootState, 'fleetView'>) =>
  effectiveSort(state.fleetView.sort, state.fleetView.devices);

export default fleetViewSlice.reducer;
